import { ReactElement } from 'react'

import { useAtomValue } from 'jotai'

import { useMutation } from '@sequencehq/api'
import {
  SequenceMenu,
  TextInputField,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { isEmail, required } from '@sequencehq/utils/validation'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { invoiceIdAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { usePermissions } from 'lib/rbac'

export const MenuItemSendTestInvoice = () => {
  const { can } = usePermissions()

  if (!can('org.invoice:send')) {
    return null
  }

  return (
    <DialogSendTestInvoice
      trigger={
        <SequenceMenu.Item
          uuid="send-test-invoice"
          label="Send test invoice"
          data-testid="header.kebab-menu.send-test-invoice"
        />
      }
    />
  )
}

type EmailFormFields = {
  email: string
}

const DialogSendTestInvoice = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()
  const invoiceId = useAtomValue(invoiceIdAtom)

  const sendTestInvoiceMutation = useMutation(
    dashboard20240730Client.postSendTestInvoice,
    {
      onSuccess: () => {
        displayNotification('Test invoice sent', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to send test invoice', { type: 'error' })
      }
    }
  )

  const { fields, queries } = useForm<EmailFormFields>({
    value: { email: '' },
    fieldConfiguration: [
      {
        property: 'email',
        validation: [required, isEmail]
      }
    ]
  })

  return (
    <SimpleModal
      data-testid="modal.send-test-invoice"
      trigger={trigger}
      variant="default"
      title="Send test invoice"
      submitButtonText="Send test invoice"
      submittingButtonText="Sending..."
      submitDisabled={!queries.isValid}
      onSubmit={async () => {
        if (!invoiceId) return false
        const res = await sendTestInvoiceMutation.mutateAsync({
          id: invoiceId,
          testEmail: fields.email.value
        })
        return Boolean(res)
      }}
      minHeight="126px"
    >
      <TextInputField
        label="Email address"
        value={fields.email.value}
        onChange={e => fields.email.onChange(e.target.value)}
        validationErrors={fields.email.validationErrors}
        data-testid="modal.send-test-invoice.email"
        placeholder="test@sequencehq.com"
      />
    </SimpleModal>
  )
}
